import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

export default function LoginPage({ onLoginSuccess }) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleMicrosoftCallback = useCallback(async (code, state) => {
    console.log('handleMicrosoftCallback iniciado')
    // Prevenir llamadas duplicadas
    if (loading) {
      console.log('Ya hay una llamada en progreso, ignorando')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const redirectUri = `${window.location.origin}/login`
      console.log('Obteniendo auth_flow de sessionStorage')
      const msalFlow = sessionStorage.getItem('msal_auth_flow')
      let flowObj = null
      if (msalFlow) {
        flowObj = JSON.parse(msalFlow)
        sessionStorage.removeItem('msal_auth_flow')
        console.log('auth_flow recuperado y eliminado de sessionStorage')
      } else {
        console.warn('No se encontró auth_flow en sessionStorage')
      }

      const payload = {
        code,
        redirect_uri: redirectUri
      }

      if (state) payload.state = state
      if (flowObj) payload.auth_flow = flowObj

      console.log('Enviando solicitud a /api/auth/microsoft/token')
      const response = await axios.post('/api/auth/microsoft/token', payload)
      console.log('Respuesta recibida:', response.data)

      if (response.data.token) {
        const token = response.data.token
        localStorage.setItem('auth_token', token)
        localStorage.setItem('user', JSON.stringify(response.data.user))
        console.log('Token guardado en localStorage, llamando onLoginSuccess')
        onLoginSuccess?.(token, response.data.user)
        window.history.replaceState({}, document.title, window.location.pathname)
        // Redirigir a la página principal después del login exitoso
        console.log('Navegando a /tarifas')
        navigate('/tarifas', { replace: true })
      }
    } catch (err) {
      console.error('Error en callback de Microsoft:', err)
      setError(err.response?.data?.error || 'Error al validar sesión de Microsoft. Verifica tu configuración.')
      window.history.replaceState({}, document.title, window.location.pathname)
    } finally {
      setLoading(false)
    }
  }, [onLoginSuccess, navigate, loading])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const state = params.get('state')
    const errorParam = params.get('error')
    const errorDesc = params.get('error_description')

    console.log('LoginPage useEffect - params:', { code: !!code, state: !!state, errorParam, errorDesc })
    
    if (code) {
      console.log('Procesando callback de Microsoft con código de autorización')
      handleMicrosoftCallback(code, state)
    } else if (errorParam) {
      setError(`Error de Microsoft: ${errorDesc || errorParam}`)
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [handleMicrosoftCallback])

  const handleLogin = async () => {
    setLoading(true)
    setError(null)
    try {
      const redirectUri = `${window.location.origin}/login`
      const queryParams = new URLSearchParams({ redirect_uri: redirectUri })
      const response = await axios.get(`/api/auth/microsoft/login?${queryParams.toString()}`)

      if (response.data.flow) {
        sessionStorage.setItem('msal_auth_flow', JSON.stringify(response.data.flow))
        const authUrl = response.data.auth_url || response.data.flow.auth_uri
        if (authUrl) {
          window.location.href = authUrl
        }
      }
    } catch (err) {
      console.error('Error al iniciar sesión:', err)
      setError(
        err.response?.data?.error || 
        'Error de configuración: Las variables de entorno de Azure AD no están configuradas correctamente.'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img 
            src="https://imaginaenergia.com/wp-content/uploads/2020/08/cropped-LOGO-IMAGINA-ok-01-1.png.webp" 
            alt="Imagina Energía" 
            className="h-16"
          />
        </div>

        {/* Título */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Liquidador de Comisiones
          </h1>
          <p className="text-gray-600">
            Inicia sesión con tu cuenta de Microsoft
          </p>
        </div>

        {/* Mensaje de error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Botón de login */}
        <button
          onClick={handleLogin}
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-3 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 23 23" fill="none">
                <path d="M0 0h11v11H0z" fill="#f25022"/>
                <path d="M12 0h11v11H12z" fill="#00a4ef"/>
                <path d="M0 12h11v11H0z" fill="#7fba00"/>
                <path d="M12 12h11v11H12z" fill="#ffb900"/>
              </svg>
              Iniciar sesión con Microsoft
            </>
          )}
        </button>

        {/* Información adicional */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Acceso exclusivo para personal autorizado</p>
          <p className="mt-2">© 2024 Imagina Energía</p>
        </div>
      </div>
    </div>
  )
}
